import { uniqueArray } from 'src/shared/lib/utils';
import { Entities } from 'src/features/messages/types/message.types';
import { Tag } from 'src/shared/types/model.types';

/**
 * Extracts all entity values from the given Entities object. Null keys are omitted
 * @param entities - The Entities object to extract values from.
 * @returns An array of objects containing the path and value of each entity.
 * @example
 * const entities = {
 *   model1: {
 *     tag1: ['value1', 'value2'],
 *     tag2: { nestedTag: ['value3'] }
 *   },
 *   model2: {
 *     tag3: ['value4']
 *   }
 * };
 * const result = getAllEntityValues(entities);
 * Result:
 * [
 *   { path: 'model1.tag1.0', value: 'value1' },
 *   { path: 'model1.tag1.1', value: 'value2' },
 *   { path: 'model1.tag2.nestedTag.0', value: 'value3' },
 *   { path: 'model2.tag3.0', value: 'value4' }
 * ]
 */
export function flatEntities(entities: Entities): Record<string, string> {
  let values: Record<string, string> = {};

  function isPrimitive(value: unknown): value is string | number | boolean {
    const t = typeof value;
    return t === 'string' || t === 'number' || t === 'boolean';
  }

  function abstractEntities(entities: Entities[string], currentPath: string) {
    if (!entities) return;

    if (isPrimitive(entities)) {
      values[currentPath] = String(entities);
      return;
    }

    for (const [tagInternalId, tagEntities] of Object.entries(entities)) {
      const newPath = currentPath ? `${currentPath}.${tagInternalId}` : tagInternalId;

      if (Array.isArray(tagEntities)) {
        tagEntities.forEach((value, index) => {
          if (isPrimitive(value)) {
            values[`${newPath}.${index}`] = String(value);
          } else {
            abstractEntities(value, `${newPath}.${index}`);
          }
        });
      } else if (typeof tagEntities === 'object' && tagEntities !== null) {
        abstractEntities(tagEntities, newPath);
      } else if (isPrimitive(tagEntities)) {
        values[newPath] = String(tagEntities);
      }
    }
  }

  for (const [modelKey, modelEntities] of Object.entries(entities)) {
    abstractEntities(modelEntities, modelKey);
  }

  return values;
}

export function sortEntities(entities: Entities[string]) {
  return Object.entries(entities).sort(([, a], [, b]) => {
    // Get types of values
    let aType = Array.isArray(a) ? 'array' : typeof a;
    let bType = Array.isArray(b) ? 'array' : typeof b;

    // Define priority order (string: 0, array with strings: 1, array with objects: 2, object: 3)
    const priority = {
      string: 0,
      arrayString: 1,
      arrayObject: 2,
      object: 3,
    };

    // Get priorities (default to 3 for any other types)
    let aPriority = aType in priority ? priority[aType as keyof typeof priority] : 3;
    let bPriority = bType in priority ? priority[bType as keyof typeof priority] : 3;

    // For arrays, check first element type
    if (aType === 'array') {
      // @ts-ignore
      aPriority = a.length > 0 && typeof a[0] === 'string' ? priority.arrayString : priority.arrayObject;
    }
    if (bType === 'array') {
      // @ts-ignore
      bPriority = b.length > 0 && typeof b[0] === 'string' ? priority.arrayString : priority.arrayObject;
    }

    // If priorities are different, sort based on priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If both are arrays of strings, sort based on the length of the first string
    if (aPriority === priority.arrayString && bPriority === priority.arrayString) {
      // @ts-ignore
      return a[0].length - b[0].length;
    }

    // For all other cases, maintain original order
    return 0;
  });
}

export function addEntity(entities: Entities, path: string, value: string): Entities {
  let result = JSON.parse(JSON.stringify(entities));
  let pathParts = path.split('.');
  let current: any = result;

  // Create nested structure if it doesn't exist
  for (let i = 0; i < pathParts.length - 1; i++) {
    const key = pathParts[i];
    const nextKey = pathParts[i + 1];

    // Initialize the current level if it doesn't exist
    if (!current[key]) {
      // If the next key is a number, initialize as array
      current[key] = !isNaN(Number(nextKey)) ? [] : {};
    }

    // If we need an array at this level but have an object, convert to array
    if (!isNaN(Number(nextKey)) && !Array.isArray(current[key])) {
      current[key] = [];
    }

    current = current[key];
  }

  let finalKey = pathParts[pathParts.length - 1];

  // Handle final assignment
  if (!isNaN(Number(finalKey))) {
    // We're setting a specific array index
    const index = Number(finalKey);
    if (Array.isArray(current)) {
      current[index] = [value];
    }
  } else {
    // We're setting an object property
    if (!current[finalKey] || current[finalKey] === null) {
      // If doesn't exist or is null, set directly
      current[finalKey] = [value];
    } else if (Array.isArray(current[finalKey])) {
      // If it's already an array, append
      current[finalKey].push(value);
    } else {
      // Convert to array with both values
      current[finalKey] = [current[finalKey], value];
    }
  }

  return result;
}

export function deleteEntity(entities: Entities, path: string): Entities {
  let result = JSON.parse(JSON.stringify(entities));
  let pathParts = path.split('.');
  let current: any = result;
  let parent: any = null;
  let parentKey: string | null = null;

  // Navigate to the parent of the target property
  for (let i = 0; i < pathParts.length - 1; i++) {
    const key = pathParts[i];
    if (!current[key]) return result; // Path doesn't exist, return unchanged
    parent = current;
    parentKey = key;
    current = current[key];
  }

  let finalKey = pathParts[pathParts.length - 1];

  // Handle array index or object property
  if (!isNaN(Number(finalKey))) {
    if (Array.isArray(current)) {
      if (current.length === 1) {
        // If this is the last element in the array, set parent's property to null
        if (parent && parentKey) {
          parent[parentKey] = null;
        }
      } else {
        current.splice(Number(finalKey), 1);
      }
    }
  } else {
    current[finalKey] = null;
  }

  return cleanupEmptyObjects(result);
}

export function cleanupEmptyObjects(entities: Entities): Entities {
  let result = JSON.parse(JSON.stringify(entities));

  function isEmptyObject(obj: any): boolean {
    if (typeof obj !== 'object' || obj === null) return false;
    if (Object.keys(obj).length === 0) return true;
    return Object.values(obj).every((value) => value === null);
  }

  function cleanup(obj: any) {
    if (!obj || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
      obj = obj.map((item) => cleanup(item));
      return obj.filter((item: any) => !isEmptyObject(item));
    }

    for (const key in obj) {
      obj[key] = cleanup(obj[key]);
      if (Array.isArray(obj[key]) && obj[key].every(isEmptyObject)) {
        obj[key] = [];
      }
    }

    return obj;
  }

  return cleanup(result);
}

/**
 * Adds a new empty object to an array for a given tag ID, initializing all nested values to null
 * @param entities - The current entities object
 * @param modelId - The model ID containing the tag
 * @param tagId - The tag ID to add a new empty object to
 * @param tags - The tags schema defining the structure
 * @returns Updated entities object with the new empty object added
 */
export function addEmptyObjectToTag(entities: Entities, modelId: string, tagId: string, tags: Tag[]): Entities {
  // Create a deep copy of entities
  const result = JSON.parse(JSON.stringify(entities));

  // Find the tag in the schema
  const tag = tags.find((t) => t.internalId === tagId);
  if (!tag || tag.outputType !== 'object[]') {
    return result;
  }

  // Initialize the path if it doesn't exist
  if (!result[modelId]) {
    result[modelId] = {};
  }
  if (!result[modelId][tagId]) {
    result[modelId][tagId] = [];
  }

  // Create empty object with null values for all child tags
  function createEmptyObject(parentTag: Tag): Record<string, any> {
    const obj: Record<string, any> = {};

    // Get all direct children tags
    const childTags = tags.filter((t) => t.parentId === parentTag.id);

    for (const childTag of childTags) {
      if (childTag.children.length > 0) {
        // If the child has its own children, create a nested object
        obj[childTag.internalId] = createEmptyObject(childTag);
      } else {
        // For leaf nodes, set null value
        obj[childTag.internalId] = null;
      }
    }

    return obj;
  }

  // Add the new empty object to the array
  const emptyObject = createEmptyObject(tag);
  result[modelId][tagId].push(emptyObject);

  return result;
}

export function getTagFromPath(path: string, tags: Tag[]): Tag | undefined {
  if (!path) {
    throw new Error('Path is required');
  }
  if (!tags) {
    throw new Error('Tags are required');
  }

  // Get the internal ID from the path, which is the last non-numeric part
  const internalId = path
    .split('.')
    .filter((p) => isNaN(parseInt(p)))
    .slice(-1)[0];

  return tags.find((tag) => tag.internalId === internalId);
}

export function getPrettyPath(path: string): string[] {
  return path
    ?.split('.')
    .slice(1)
    .filter((p) => isNaN(parseInt(p)));
}

export function cleanNullValues(entities: Entities): Entities {
  if (!entities) return {};
  return Object.entries(entities).reduce((acc, [modelId, modelEntities]) => {
    // Skip if no entities for this model
    if (!modelEntities) return acc;

    // Clean each entity object recursively
    const cleanedEntities = Object.entries(modelEntities).reduce(
      (modelAcc, [tagId, tagValues]) => {
        if (tagValues === null || tagValues === undefined) return modelAcc;

        // Handle array of values
        if (Array.isArray(tagValues)) {
          const cleanedValues = (tagValues as any[]).filter((v) => v !== null);
          if (cleanedValues.length > 0) {
            modelAcc[tagId] = cleanedValues;
          }
        }
        // Handle nested object
        else if (typeof tagValues === 'object') {
          const cleanedObj = cleanNullValues({ temp: tagValues }).temp;
          if (Object.keys(cleanedObj || {}).length > 0) {
            modelAcc[tagId] = cleanedObj;
          }
        }
        // Preserve primitive values (strings, numbers, booleans)
        else {
          modelAcc[tagId] = tagValues;
        }
        return modelAcc;
      },
      {} as Record<string, any>
    );

    // Only add model if it has cleaned entities
    if (Object.keys(cleanedEntities).length > 0) {
      acc[modelId] = cleanedEntities;
    }
    return acc;
  }, {} as Entities);
}

export function getEntityValue(entities: Entities[string], path: string[]): Entities[string] {
  let entitiesDeepCopy = JSON.parse(JSON.stringify(entities)) as Entities[string];
  let current = entitiesDeepCopy;

  // Handle all but last path segment
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];

    // Create nested object/array if it doesn't exist
    if (!current[key]) {
      // If next key is a number, create array, otherwise object
      const nextKey = path[i + 1];
      current[key] = !isNaN(parseInt(nextKey)) ? [] : {};
    }

    // Type assertion to handle the null case
    current = current[key] as Entities[string];
  }

  // Set value as string array at final path segment
  const lastKey = path[path.length - 1];
  return current[lastKey] as Entities[string];
}

export function setEntityValue(
  entities: Entities[string],
  path: string[],
  value: Entities[string][string]
): Entities[string] {
  let result = JSON.parse(JSON.stringify(entities));
  let current = result;

  // Handle all but last path segment
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];

    // Create nested object/array if it doesn't exist
    if (!current[key]) {
      // If next key is a number, create array, otherwise object
      const nextKey = path[i + 1];
      current[key] = !isNaN(parseInt(nextKey)) ? [] : {};
    }

    if (current[key] === null) {
      current[key] = {};
    }
    current = current[key];
  }

  // Set value as string array at final path segment
  // Use !== undefined check to preserve falsy values like false, 0, ""
  const lastKey = path[path.length - 1];
  current[lastKey] = value !== undefined ? value : null;

  if (current.length === 1 && current[0] === null) {
    current = null;
  }

  return result;
}

export function filterEntities(entities: Entities[string][string]): boolean {
  if (!entities) {
    return false;
  }

  if (Array.isArray(entities) && (entities.length === 0 || (entities as any[]).every((item) => item === null))) {
    return false;
  }

  const isEmpty = Object.keys(entities || {}).length === 0;
  const isAllNull = Object.values(entities || {}).every((item) => item === null);
  if (typeof entities === 'object' && (isEmpty || isAllNull)) {
    return false;
  }

  return true;
}

/**
 * Generates a JSON schema for a given tag and its children
 * @param tag - The tag to generate a schema for
 * @param allTags - All available tags to look up children
 * @returns A JSON schema object representing the tag's structure
 */
export function getTagJsonSchema(tag: Tag, allTags: Tag[]): object {
  // Base case: tag has no children
  if (!tag?.children || tag.children.length === 0) {
    return {
      type: 'array',
      items: { type: 'string' },
    };
  }

  // Get all direct children tags
  const childTags = allTags.filter((t) => t.parentId === tag.id);

  // Build properties object from child tags
  const properties = childTags.reduce(
    (acc, childTag) => {
      acc[childTag.internalId] = getTagJsonSchema(childTag, allTags);
      return acc;
    },
    {} as Record<string, any>
  );

  // If tag is grouped, return array of objects
  if (tag.isGrouped) {
    return {
      type: 'array',
      items: {
        type: 'object',
        properties,
        additionalProperties: false,
      },
    };
  }

  // Otherwise return single object
  return {
    type: 'object',
    properties,
    additionalProperties: false,
  };
}

export type EntityType = 'PRIMITIVE' | 'ARRAY' | 'OBJECT';
export function getEntityType(value: any, tag: Tag): EntityType {
  const hasChildren = tag?.children.length > 0;

  if (!hasChildren) {
    return 'PRIMITIVE';
  }

  const isGrouped = tag?.isGrouped;
  if (value === null) {
    // if the value is null, we can only check based on the config of the tag
    return isGrouped ? 'ARRAY' : 'OBJECT';
  }

  const isArray = Array.isArray(value);
  return isArray ? 'ARRAY' : 'OBJECT';
}

export function extractPrimitiveChildren(tags: Tag[], parentTag?: Tag) {
  if (!parentTag) {
    return tags.filter((tag) => !tag.children?.length && !tag.parentId); // root tags with no children
  }
  return tags.filter((tag) => tag.parentId === parentTag.id && !tag.children?.length); // children of the parent tag with no children
}

export function getMissingsTags(entities: Entities[string], tags: Tag[]) {
  return uniqueArray(tags, 'internalId').filter((tag) => !entities?.[tag.internalId]?.length);
}
